import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  User,
  Key,
  Bell,
  Shield,
  ChevronRight,
  PlugZap,
  HelpCircle,
  Package,
} from "lucide-react";

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [settings, setSettings] = useState({
    notifications_email: true,
    notifications_browser: false,
    auto_invoice: true,
    auto_cargo_label: true,
  });
  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
    company_name: user?.company_name || "",
  });

  useEffect(() => {
    setProfileForm({
      full_name: user?.full_name || "",
      email: user?.email || "",
      company_name: user?.company_name || "",
    });
  }, [user?.full_name, user?.email, user?.company_name]);


  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await api.patch("/auth/me", {
        full_name: profileForm.full_name?.trim(),
        email: profileForm.email?.trim(),
        company_name: profileForm.company_name?.trim(),
      });
      await refreshUser();
      toast.success("Profil güncellendi!");
    } catch (error) {
      toast.error(error.message || "Profil güncellenemedi");
    } finally {
      setLoading(false);
    }
  };

  const initials = (user?.full_name || user?.email || "?")
    .split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div className="page-shell" data-testid="settings-page">
      <div className="page-header mb-6">
        <div className="app-page-header">
          <h1 className="app-page-title">Ayarlar</h1>
          <p className="app-page-subtitle">Hesap ve uygulama ayarlarınızı yönetin.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">
        {/* ── Sol: form kartları ── */}
        <div className="space-y-5">

          {/* Profile Settings */}
          <Card className="surface-card-soft">
            <CardHeader>
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Profil Bilgileri</CardTitle>
              </div>
              <CardDescription>Kişisel ve şirket bilgilerinizi güncelleyin.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Ad Soyad</Label>
                  <Input
                    id="full_name"
                    value={profileForm.full_name}
                    onChange={(e) => setProfileForm((p) => ({ ...p, full_name: e.target.value }))}
                    data-testid="settings-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-posta</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm((p) => ({ ...p, email: e.target.value }))}
                    data-testid="settings-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_name">Şirket Adı</Label>
                <Input
                  id="company_name"
                  value={profileForm.company_name}
                  onChange={(e) => setProfileForm((p) => ({ ...p, company_name: e.target.value }))}
                  data-testid="settings-company-input"
                />
              </div>
              <Button onClick={handleSaveProfile} disabled={loading} data-testid="save-profile-btn">
                {loading ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
              </Button>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card className="surface-card-soft">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Bildirimler</CardTitle>
              </div>
              <CardDescription>Bildirim tercihlerinizi ayarlayın.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">E-posta Bildirimleri</p>
                  <p className="text-sm text-muted-foreground">Yeni sipariş ve güncellemeler için e-posta alın.</p>
                </div>
                <Switch
                  checked={settings.notifications_email}
                  onCheckedChange={(checked) => setSettings({ ...settings, notifications_email: checked })}
                  data-testid="email-notifications-switch"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Tarayıcı Bildirimleri</p>
                  <p className="text-sm text-muted-foreground">Tarayıcı üzerinden anlık bildirim alın.</p>
                </div>
                <Switch
                  checked={settings.notifications_browser}
                  onCheckedChange={(checked) => setSettings({ ...settings, notifications_browser: checked })}
                  data-testid="browser-notifications-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Automation */}
          <Card className="surface-card-soft">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Otomasyon</CardTitle>
              </div>
              <CardDescription>Otomatik işlem ayarlarınızı yönetin.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Otomatik Fatura Oluşturma</p>
                  <p className="text-sm text-muted-foreground">Sipariş tamamlandığında otomatik fatura oluştur.</p>
                </div>
                <Switch
                  checked={settings.auto_invoice}
                  onCheckedChange={(checked) => setSettings({ ...settings, auto_invoice: checked })}
                  data-testid="auto-invoice-switch"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Otomatik Kargo Etiketi</p>
                  <p className="text-sm text-muted-foreground">Sipariş onaylanınca otomatik kargo etiketi oluştur.</p>
                </div>
                <Switch
                  checked={settings.auto_cargo_label}
                  onCheckedChange={(checked) => setSettings({ ...settings, auto_cargo_label: checked })}
                  data-testid="auto-cargo-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* API Keys */}
          <Card className="surface-card-soft">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Key className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Entegrasyon API Anahtarları</CardTitle>
              </div>
              <CardDescription>Mağaza ve pazaryeri bağlantıları için API anahtarları.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 rounded-lg bg-muted/50 text-sm">
                <p className="text-muted-foreground">
                  API anahtarlarınızı görmek ve yönetmek için <strong>Entegrasyonlar</strong> sayfasından ilgili entegrasyonun ayarlarına gidin.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Sağ: bilgi paneli ── */}
        <div className="space-y-4 lg:sticky lg:top-6">
          {/* Hesap kartı */}
          <Card className="surface-card-soft overflow-hidden">
            <div className="h-[3px] w-full" style={{ background: "linear-gradient(90deg, #1487a3 0%, #22c8e0 100%)" }} />
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-11 w-11 rounded-full bg-[#1487a3]/15 border border-[#1487a3]/25 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-[#1487a3]">{initials}</span>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-foreground text-sm truncate">{user?.full_name || "—"}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email || ""}</p>
                </div>
              </div>
              {user?.company_name && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/60 text-xs text-muted-foreground">
                  <Package className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{user.company_name}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Hızlı bağlantılar */}
          <Card className="surface-card-soft">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Hızlı Erişim</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-1">
              {[
                { label: "Entegrasyonlar", path: "/panel/entegrasyonlar", icon: PlugZap },
                { label: "Ürün Yönetimi", path: "/panel/urunler", icon: Package },
              ].map(({ label, path, icon: Icon }) => (
                <button
                  key={path}
                  type="button"
                  onClick={() => navigate(path)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm text-foreground hover:bg-accent/60 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    {label}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Yardım */}
          <Card className="surface-card-soft">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                  <HelpCircle className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Yardıma mı ihtiyacınız var?</p>
                  <p className="text-xs text-muted-foreground mt-1">Destek ekibimize ulaşmak için sağ üst köşedeki Destek butonunu kullanın.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
